import type { VisibilityNumber } from "features/editor/sandboxUtils";
import { useForkSandbox } from "features/forkSandbox";
import type { Editor } from "features/types/editor";
import { Button, Icon, Stack, Text } from "prism-react";
import { useEffect } from "react";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useActiveWorkspaceSubscription } from "hooks/useActiveWorkspaceSubscription";
import { useAnalytics } from "hooks/useAnalytics";
import { paths } from "utils/router";

export const OutOfCreditsDescription: React.FC<{ editor: Editor }> = ({
  editor,
}) => {
  const activeWorkspace = useActiveWorkspace();
  const activeSubscription = useActiveWorkspaceSubscription();
  const track = useAnalytics();

  const isOwnWorkspace =
    activeWorkspace && editor.workspace?.id === activeWorkspace.id;

  const canBuyPro = Boolean(
    isOwnWorkspace &&
      activeSubscription &&
      activeSubscription.isTeamAdmin &&
      activeSubscription.isFree,
  );
  useEffect(() => {
    track("frozen_message", {
      canBuyPro,
    });
  }, [canBuyPro]);

  if (!isOwnWorkspace) {
    // Contact admin to upgrade
    return (
      <Stack gap={6}>
        <span>
          The workspace of the owner of this VM has run out of credits. Contact
          them to unlock the VM.
        </span>
        {editor.type === "sandbox" && (
          <span>
            You can still fork this Devbox to your workspace to see it.
          </span>
        )}
      </Stack>
    );
  }

  let description = `Your workspace has run out of credits. Your Devboxes and repositories have been frozen.`;
  if (activeSubscription && activeWorkspace) {
    const isFreeSub = activeSubscription.isFree;
    if (isFreeSub) {
      // Mention free plan
      description = `Your workspace “${activeWorkspace?.name}” has run out of credits on the Free plan. Your Devboxes and repositories have been frozen.`;
    } else {
      // Assume it's a pro plan
      description = `Your workspace “${activeWorkspace?.name}” has run out of credits. Your Devboxes and repositories have been frozen.`;
    }

    const action = isFreeSub
      ? "upgrade to a Pro plan"
      : "update the current Pro plan";
    if (!activeSubscription.isTeamAdmin) {
      // Contact admin to upgrade
      description += ` To continue using CodeSandbox, ask an admin of your workspace to ${action}.`;
    } else {
      description += ` To continue using CodeSandbox, you can ${action}.`;
    }

    return (
      <Stack gap={6}>
        <span>{description}</span>

        <Stack css={{ color: "$informative-success-base" }} horizontal>
          <Icon size={4} type="check" />
          <Text>All your work is saved.</Text>
        </Stack>
      </Stack>
    );
  }
};

export const OutOfCreditsButton: React.FC<{ editor: Editor }> = ({
  editor,
}) => {
  const activeWorkspace = useActiveWorkspace();
  const activeSubscription = useActiveWorkspaceSubscription();
  const forkSandboxDispatcher = useForkSandbox();

  const isOwnWorkspace =
    activeWorkspace && editor.workspace?.id === activeWorkspace.id;

  if (!isOwnWorkspace && editor.type === "sandbox") {
    const [fork, dispatchFork] = forkSandboxDispatcher;

    const forkButton = (
      <Button
        loading={fork.state === "FORKING"}
        onClick={() => {
          const privacy: VisibilityNumber = editor.privacy;
          const collectionId = editor.collection?.id;

          dispatchFork({
            type: "CREATE_FORK",
            workspaceId: activeWorkspace?.id,
            privacy,
            collectionId,
          });
        }}
        variant="primary"
      >
        Fork
      </Button>
    );

    if (fork.state === "CREATE_FORK_ERROR") {
      return (
        <>
          <Text>Failed to fork: {fork.message}</Text>

          {forkButton}
        </>
      );
    }

    // Give the option to fork the devbox if it's a devbox
    return forkButton;
  }

  let recoverButton: React.ReactNode = null;

  if (activeSubscription.isTeamAdmin && activeWorkspace) {
    const buttonText = activeSubscription.isFree
      ? "Upgrade to Pro"
      : "Manage Subscription";

    recoverButton = (
      <Button
        as="a"
        href={
          activeSubscription.isFree
            ? paths.upgrade({
                workspaceId: activeWorkspace.id,
                source: "frozen_editor",
              })
            : paths.manageBilling({
                workspaceId: activeWorkspace.id,
                source: "frozen_editor",
              })
        }
        variant="primary"
      >
        {buttonText}
      </Button>
    );
  }

  return recoverButton;
};
